import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Table, Icon } from 'semantic-ui-react';
import { format } from 'date-fns';
import { getIngestionHistory, type IngestionHistoryEvent } from '~/api/ingestion';
import type { DocumentIngestionResult } from './types';
import { JsonHighlighted } from './JsonHighlighted';

const COLUMN_COUNT = 4;

const monoPreStyle: React.CSSProperties = {
  fontSize: '0.85em',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  marginTop: 'var(--xx-small)',
  padding: 'var(--x-small)',
  background: 'var(--color-neutral-100, #f5f5f5)',
  borderRadius: 4,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

function ErrorDetailsJson({ details }: { details: unknown }) {
  if (typeof details === 'string') {
    try {
      const parsed = JSON.parse(details) as unknown;
      if (parsed !== null && typeof parsed === 'object') {
        return (
          <JsonHighlighted
            value={parsed}
            style={{
              marginTop: 'var(--xx-small)',
              background: 'var(--color-neutral-100, #f5f5f5)',
            }}
          />
        );
      }
    } catch {
      // not JSON
    }
    return <pre style={monoPreStyle}>{details}</pre>;
  }
  return (
    <JsonHighlighted
      value={details}
      style={{
        marginTop: 'var(--xx-small)',
        background: 'var(--color-neutral-100, #f5f5f5)',
      }}
    />
  );
}

function EventDetails({ event }: { event: IngestionHistoryEvent }) {
  const result = event.result;
  if (!result || typeof result !== 'object') {
    return <p style={{ margin: 0, color: 'var(--color-neutral-600)' }}>No result data.</p>;
  }

  if (event.status === 'SUCCESS') {
    const success = result as DocumentIngestionResult;
    const updatePayload = success.updatePayload;
    const debugSummary = success.debugSummary;
    return (
      <div style={{ padding: 'var(--x-small) 0' }}>
        {updatePayload != null && Object.keys(updatePayload).length > 0 && (
          <details open>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
              Payload sent to update
            </summary>
            <JsonHighlighted
              value={updatePayload}
              style={{
                marginTop: 'var(--xx-small)',
                background: 'var(--color-neutral-100, #f5f5f5)',
              }}
            />
          </details>
        )}
        {debugSummary != null && (
          <details open style={{ marginTop: 'var(--x-small)' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
              Missing fields ({debugSummary.missing.length})
            </summary>
            <JsonHighlighted
              value={debugSummary.missing}
              style={{
                marginTop: 'var(--xx-small)',
                background: 'var(--color-neutral-100, #f5f5f5)',
              }}
            />
          </details>
        )}
      </div>
    );
  }

  if (event.status === 'ERROR') {
    const err = result as { message?: string; details?: unknown };
    const message = err?.message ?? 'Unknown error';
    const details = err?.details;
    return (
      <div style={{ padding: 'var(--x-small) 0' }}>
        <p style={{ margin: 0 }}>{message}</p>
        {details != null && (
          <ErrorDetailsJson details={details} />
        )}
      </div>
    );
  }

  return (
    <p style={{ margin: 0, color: 'var(--color-neutral-600)' }}>
      Status: {event.status}
    </p>
  );
}

export default function IngestionHistoryTable({ projectId }: { projectId: string }) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['ingestionHistory', projectId],
    queryFn: () => getIngestionHistory(projectId),
    enabled: !!projectId,
  });

  const events = data?.events ?? [];

  const toggle = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  if (isLoading) {
    return (
      <div className="card" style={{ marginTop: 'var(--medium)' }}>
        <h4>Import history</h4>
        <p style={{ color: 'var(--color-neutral-600)' }}>Loading…</p>
      </div>
    );
  }

  if (events.length === 0) {
    return (
      <div className="card" style={{ marginTop: 'var(--medium)' }}>
        <h4>Import history</h4>
        <p style={{ color: 'var(--color-neutral-600)' }}>No imports yet.</p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginTop: 'var(--medium)' }}>
      <h4>Import history</h4>
      <Table compact size="small" simple>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1} style={{ paddingLeft: 'var(--xx-small)' }} />
            <Table.HeaderCell>Date</Table.HeaderCell>
            <Table.HeaderCell>File</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {events.map((event) => {
            const isExpanded = expandedId === event.id;
            return (
              <React.Fragment key={event.id}>
                <Table.Row
                  style={{ cursor: 'pointer' }}
                  onClick={() => toggle(event.id)}
                >
                  <Table.Cell collapsing style={{ paddingLeft: 'var(--xx-small)' }}>
                    <Icon name={isExpanded ? 'chevron down' : 'chevron right'} style={{ marginLeft: '8px' }}/>
                  </Table.Cell>
                  <Table.Cell>
                    {event.createdAt
                      ? format(new Date(event.createdAt), 'MMM d, yyyy h:mm a')
                      : '—'}
                  </Table.Cell>
                  <Table.Cell>{event.fileName ?? '—'}</Table.Cell>
                  <Table.Cell>
                    <span
                      style={{
                        padding: '2px 6px',
                        borderRadius: 4,
                        fontSize: '0.85em',
                        background:
                          event.status === 'SUCCESS'
                            ? 'var(--color-success-light, #e8f5e9)'
                            : event.status === 'ERROR'
                              ? 'var(--color-error-light, #ffebee)'
                              : 'var(--color-neutral-100, #f5f5f5)',
                        color:
                          event.status === 'SUCCESS'
                            ? 'var(--color-success, #2e7d32)'
                            : event.status === 'ERROR'
                              ? 'var(--color-error, #c62828)'
                              : 'var(--color-neutral-700, #616161)',
                      }}
                    >
                      {event.status}
                    </span>
                  </Table.Cell>
                </Table.Row>
                <Table.Row
                  style={{ display: isExpanded ? 'table-row' : 'none' }}
                >
                  <Table.Cell colSpan={COLUMN_COUNT} style={{ paddingTop: 0, paddingBottom: 0 }}>
                    <div style={{ paddingLeft: 'var(--small)' }}>
                      <EventDetails event={event} />
                    </div>
                  </Table.Cell>
                </Table.Row>
              </React.Fragment>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
}
